import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  buildCarePreparationText,
  buildVisitPreparation,
  closeVisit,
  summarizeCareSupplies,
  treatmentPlanAt,
  type CareEventGroupRecord,
  type CarePlanRecord,
  type CarePlanState,
  type CareQuestionRecord,
  type CareSupplyRecord,
  type Pet,
  type Threshold,
  type Treatment,
  type Visit,
} from '@petrhythm/core';
import { repository } from '@petrhythm/data';
import { Button, Card, EmptyState, ErrorState, SectionHeading, t } from '@petrhythm/ui';
import { downloadText } from '../lib/download.js';
import { kstDate, todayKst } from '../lib/format.js';
import { track } from '../lib/platform.js';
import { useData } from './app.js';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/u;

const STATE_LABEL: Record<CarePlanState, string> = {
  active: t('carePlan.stateActive'),
  open_ended: t('carePlan.stateOpenEnded'),
  recheck_due: t('carePlan.stateRecheck'),
  expired: t('carePlan.stateExpired'),
};

function usePet(): Pet | undefined {
  const { petId } = useParams();
  const { pets } = useData();
  return pets.find((pet) => pet.id === petId);
}

/** 쉼표로 적은 날짜를 형식이 맞는 것만 남겨 정렬한다. 형식이 틀린 값은 조용히 통과시키지 않고 버린다. */
function parseDates(raw: string): string[] {
  return [...new Set(raw.split(',').map((value) => value.trim()).filter((value) => DATE_PATTERN.test(value)))].sort();
}

export function CarePlanScreen() {
  const pet = usePet();
  const [plans, setPlans] = useState<CarePlanRecord[]>([]);
  const [questions, setQuestions] = useState<CareQuestionRecord[]>([]);
  const [groups, setGroups] = useState<CareEventGroupRecord[]>([]);
  const [visitDates, setVisitDates] = useState<string[]>([]);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [supplies, setSupplies] = useState<CareSupplyRecord[]>([]);
  const [message, setMessage] = useState('');
  const [openForm, setOpenForm] = useState<'plan' | 'question' | 'group' | 'threshold' | 'supply' | null>(null);
  const [thresholdValue, setThresholdValue] = useState('');
  const [thresholdDirection, setThresholdDirection] = useState<'above' | 'below'>('above');

  const [planTitle, setPlanTitle] = useState('');
  const [planSource, setPlanSource] = useState('');
  const [planCheckedOn, setPlanCheckedOn] = useState(todayKst());
  const [planValidUntil, setPlanValidUntil] = useState('');
  const [planRecheckOn, setPlanRecheckOn] = useState('');

  const [questionText, setQuestionText] = useState('');
  const [groupTitle, setGroupTitle] = useState('');
  const [groupDates, setGroupDates] = useState('');
  const [supplyName, setSupplyName] = useState('');
  const [supplyRemaining, setSupplyRemaining] = useState('');
  const [supplyUnit, setSupplyUnit] = useState('개');
  const [supplyCheckedOn, setSupplyCheckedOn] = useState(todayKst());
  const [supplyCheckedTime, setSupplyCheckedTime] = useState(() => new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(11, 16));
  const [supplyThreshold, setSupplyThreshold] = useState('');
  const [editingSupplyId, setEditingSupplyId] = useState<string | null>(null);
  const [supplyParams] = useSearchParams();
  const [supplyTreatmentId, setSupplyTreatmentId] = useState(supplyParams.get('supply') ?? '');
  const [supplyPerDose, setSupplyPerDose] = useState('');
  const [supplyNotify,setSupplyNotify]=useState(false);
  const [supplyDays,setSupplyDays]=useState('3');
  const [treatmentOptions, setTreatmentOptions] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => { if (supplyParams.get('supply')) setOpenForm('supply'); }, []);

  const [visitedOn, setVisitedOn] = useState(todayKst());
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const petId = pet?.id;
  const load = () => {
    if (!petId) return;
    const mine = <T extends { petId: string }>(rows: T[]) => rows.filter((row) => row.petId === petId);
    void Promise.all([
      repository.list<CarePlanRecord>('carePlans'),
      repository.list<CareQuestionRecord>('careQuestions'),
      repository.list<CareEventGroupRecord>('careEventGroups'),
      repository.list<Visit>('visits'),
      repository.list<Threshold>('thresholds'),
      repository.list<CareSupplyRecord>('careSupplies'),
      repository.list<Treatment>('treatments'),
    ]).then(([planRows, questionRows, groupRows, visitRows, thresholdRows, supplyRows, treatmentRows]) => {
      setTreatmentOptions(mine(treatmentRows).map((row) => ({ id: row.id, name: treatmentPlanAt(row, Date.now()).name })));
      setPlans(mine(planRows));
      setQuestions(mine(questionRows));
      setGroups(mine(groupRows));
      setVisitDates(mine(visitRows).map((row) => kstDate(row.visitedAt)));
      setThresholds(mine(thresholdRows).filter((row) => row.vitalType === 'sleepingRespiratoryRate'));
      setSupplies(mine(supplyRows));
    }).catch(() => setMessage(t('error.validation')));
  };
  useEffect(load, [petId]);

  const preparation = useMemo(() => buildVisitPreparation({
    today: todayKst(),
    plans: plans.map((row) => ({
      id: row.id,
      title: row.title,
      source: { label: row.sourceLabel, checkedOn: row.sourceCheckedOn },
      validUntil: row.validUntil ?? null,
      recheckOn: row.recheckOn ?? null,
    })),
    questions: questions.map((row) => ({
      id: row.id,
      text: row.text,
      askedOn: row.askedOn,
      relatedRecordIds: row.relatedRecordIds ?? [],
      answeredOn: row.answeredOn ?? null,
      answer: row.answer ?? null,
    })),
    eventGroups: groups.map((row) => ({ id: row.id, title: row.title, recordIds: row.recordIds, dates: row.dates })),
    visitDates,
  }), [plans, questions, groups, visitDates]);

  const supplySummary = useMemo(() => summarizeCareSupplies(supplies), [supplies]);
  const preparationText = useMemo(() => buildCarePreparationText({
    petName: pet?.name ?? '',
    preparedOn: todayKst(),
    plans: plans.map((row) => ({ title: row.title, sourceLabel: row.sourceLabel, sourceCheckedOn: row.sourceCheckedOn })),
    questions: preparation.openQuestions.map((row) => ({ text: row.text, askedOn: row.askedOn })),
    eventGroups: groups.map((row) => ({ title: row.title, dates: row.dates })),
    supplies,
  }), [pet?.name, plans, preparation.openQuestions, groups, supplies]);

  if (!pet) return <section className="screen container"><ErrorState messageKey="error.notFound" /></section>;

  const savePlan = async (event: FormEvent) => {
    event.preventDefault();
    if (!planTitle.trim() || !planSource.trim()) { setMessage(t('error.validation')); return; }
    try {
      await repository.put<CarePlanRecord>('carePlans', {
        petId: pet.id,
        title: planTitle.trim(),
        sourceLabel: planSource.trim(),
        sourceCheckedOn: planCheckedOn,
        validUntil: planValidUntil || null,
        recheckOn: planRecheckOn || null,
      });
      setPlanTitle(''); setPlanSource(''); setPlanValidUntil(''); setPlanRecheckOn('');
      setOpenForm(null); setMessage(t('common.saved')); track('record_saved'); load();
    } catch { setMessage(t('error.validation')); }
  };

  const saveQuestion = async (event: FormEvent) => {
    event.preventDefault();
    if (!questionText.trim()) { setMessage(t('error.validation')); return; }
    try {
      await repository.put<CareQuestionRecord>('careQuestions', {
        petId: pet.id,
        text: questionText.trim(),
        askedOn: todayKst(),
        relatedRecordIds: [],
        answeredOn: null,
        answer: null,
      });
      setQuestionText(''); setOpenForm(null); setMessage(t('common.saved')); track('record_saved'); load();
    } catch { setMessage(t('error.validation')); }
  };

  const saveGroup = async (event: FormEvent) => {
    event.preventDefault();
    const dates = parseDates(groupDates);
    if (!groupTitle.trim() || dates.length === 0) { setMessage(t('error.validation')); return; }
    try {
      await repository.put<CareEventGroupRecord>('careEventGroups', {
        petId: pet.id, title: groupTitle.trim(), recordIds: [], dates,
      });
      setGroupTitle(''); setGroupDates(''); setOpenForm(null); setMessage(t('common.saved')); track('record_saved'); load();
    } catch { setMessage(t('error.validation')); }
  };

  /**
   * 주치의가 정한 기준값을 보호자가 그대로 옮겨 적는다.
   * 우리는 기준을 계산하지 않고, 이 값이 어디서 왔는지만 화면이 표시한다(NG-DEPTH-01).
   */
  const saveThreshold = async (event: FormEvent) => {
    event.preventDefault();
    const parsed = Number(thresholdValue);
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 300) { setMessage(t('error.validation')); return; }
    try {
      await repository.put<Threshold>('thresholds', {
        petId: pet.id,
        vitalType: 'sleepingRespiratoryRate',
        direction: thresholdDirection,
        value: Math.round(parsed),
        setAt: Date.now(),
      });
      setThresholdValue(''); setOpenForm(null); setMessage(t('common.saved')); track('record_saved'); load();
    } catch { setMessage(t('error.validation')); }
  };

  const saveSupply = async (event: FormEvent) => {
    event.preventDefault();
    const remaining = Number(supplyRemaining);
    const reminderAt = supplyThreshold === '' ? null : Number(supplyThreshold);
    const perDose = supplyPerDose === '' ? undefined : Number(supplyPerDose);
    const checkedAt = supplyCheckedTime ? Date.parse(`${supplyCheckedOn}T${supplyCheckedTime}:00+09:00`) : undefined;
    if ((supplyTreatmentId && checkedAt == null) || (checkedAt != null && (!Number.isFinite(checkedAt) || checkedAt > Date.now()))) { setMessage('재고를 센 날짜와 시각을 확인해 주세요. 아직 오지 않은 시각은 저장할 수 없어요.'); return; }
    if(supplyNotify&&(!Number.isInteger(Number(supplyDays))||Number(supplyDays)<0||Number(supplyDays)>30)){setMessage(t('error.validation'));return;}
    if (!supplyName.trim() || !supplyUnit.trim() || !supplyRemaining.trim() || !Number.isFinite(remaining) || remaining < 0
      || (perDose != null && (!Number.isFinite(perDose) || perDose <= 0))
      || (reminderAt != null && (!Number.isFinite(reminderAt) || reminderAt < 0))) {
      setMessage(t('error.validation'));
      return;
    }
    try {
      await repository.put<CareSupplyRecord>('careSupplies', {
        ...(editingSupplyId ? { id: editingSupplyId } : {}),
        petId: pet.id,
        name: supplyName.trim(),
        remainingQuantity: remaining,
        unitLabel: supplyUnit.trim(),
        checkedOn: supplyCheckedOn,
        ...(checkedAt != null ? { checkedAt } : {}),
        userReminderAt: reminderAt,
        notifyEnabled:supplyNotify,reminderDays:Number(supplyDays),
        ...(supplyTreatmentId ? { treatmentId: supplyTreatmentId } : {}),
        ...(supplyTreatmentId && perDose != null ? { perDoseQuantity: perDose } : {}),
      });
      setSupplyName(''); setSupplyRemaining(''); setSupplyThreshold(''); setEditingSupplyId(null); setSupplyTreatmentId(''); setSupplyPerDose('');
      setOpenForm(null); setMessage(t('common.saved')); track('record_saved', { recordType: 'care_supply' }); load();
    } catch { setMessage(t('error.validation')); }
  };

  const editSupply = (supply: CareSupplyRecord) => {
    setEditingSupplyId(supply.id);
    setSupplyName(supply.name);
    setSupplyRemaining('');
    setSupplyUnit(supply.unitLabel);
    setSupplyCheckedOn(todayKst());
    setSupplyCheckedTime(new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(11, 16));
    setSupplyThreshold(supply.userReminderAt == null ? '' : String(supply.userReminderAt));
    setSupplyNotify(supply.notifyEnabled===true);setSupplyDays(String(supply.reminderDays??3));
    setSupplyTreatmentId(supply.treatmentId ?? ''); setSupplyPerDose(supply.perDoseQuantity == null ? '' : String(supply.perDoseQuantity));
    setOpenForm('supply');
  };

  const removeSupply = async (supply: CareSupplyRecord) => {
    await repository.softDelete<CareSupplyRecord>('careSupplies', supply.id);
    if (editingSupplyId === supply.id) {
      setEditingSupplyId(null); setOpenForm(null);
    }
    setMessage('재고 기록을 목록에서 지웠어요. 설정의 휴지통에서 되돌릴 수 있어요.');
    load();
  };

  const copyPreparation = async () => {
    try {
      await navigator.clipboard.writeText(preparationText);
      setMessage(t('carePlan.preparationCopied'));
    } catch {
      downloadText(`petrhythm-${pet.name}-${todayKst()}-care-pack.txt`, preparationText);
      setMessage('복사 권한이 없어 텍스트 파일로 저장했어요.');
    }
  };

  /** 답을 적은 질문만 닫는다. 방문했다는 이유로 나머지를 닫지 않는다. */
  const saveAnswers = async () => {
    const map = new Map(Object.entries(answers).filter(([, value]) => value.trim().length > 0));
    if (map.size === 0) { setMessage(t('carePlan.closeHint')); return; }
    const next = closeVisit({
      visitedOn,
      questions: questions.map((row) => ({
        id: row.id, text: row.text, askedOn: row.askedOn,
        relatedRecordIds: row.relatedRecordIds ?? [],
        answeredOn: row.answeredOn ?? null, answer: row.answer ?? null,
      })),
      answers: map,
    });
    try {
      for (const updated of next) {
        const original = questions.find((row) => row.id === updated.id);
        if (!original || original.answeredOn === updated.answeredOn) continue;
        await repository.put<CareQuestionRecord>('careQuestions', {
          ...original, answeredOn: updated.answeredOn, answer: updated.answer,
        });
      }
      setAnswers({}); setMessage(t('common.saved')); load();
    } catch { setMessage(t('error.validation')); }
  };

  return <section className="screen narrow container">
    <Link className="back-link" to="/app">← 오늘</Link>
    <SectionHeading eyebrow={pet.name} title={t('carePlan.title')} />
    <p className="screen-lede">{t('carePlan.lede')}</p>

    {!preparation.hasSomethingToBring && !plans.length && !supplies.length && !thresholds.length
      ? <EmptyState title={t('carePlan.nothingToBring')} action={<Button onClick={() => setOpenForm('plan')}>{t('carePlan.planNew')}</Button>} />
      : null}

    <div className="section-row">
      <h2>{t('carePlan.planSection')}</h2>
      <Button variant="ghost" onClick={() => setOpenForm(openForm === 'plan' ? null : 'plan')}>
        {openForm === 'plan' ? t('common.close') : t('carePlan.planNew')}
      </Button>
    </div>
    {preparation.planAttentionCount > 0
      ? <p className="form-hint">{t('carePlan.attention', { count: preparation.planAttentionCount })}</p>
      : null}
    {openForm === 'plan'
      ? <form className="form-card" onSubmit={(event) => void savePlan(event)}>
          <label>{t('carePlan.planTitle')}<input required value={planTitle} maxLength={120} onChange={(event) => setPlanTitle(event.target.value)} /></label>
          <label>{t('carePlan.planSource')}<input required value={planSource} maxLength={80} onChange={(event) => setPlanSource(event.target.value)} /></label>
          <p className="form-hint">{t('carePlan.planSourceHint')}</p>
          <label>{t('carePlan.planCheckedOn')}<input required type="date" value={planCheckedOn} max={todayKst()} onChange={(event) => setPlanCheckedOn(event.target.value)} /></label>
          <label>{t('carePlan.planValidUntil')}<input type="date" value={planValidUntil} onChange={(event) => setPlanValidUntil(event.target.value)} /></label>
          <label>{t('carePlan.planRecheckOn')}<input type="date" value={planRecheckOn} onChange={(event) => setPlanRecheckOn(event.target.value)} /></label>
          <Button size="lg" type="submit">{t('common.save')}</Button>
        </form>
      : null}
    {preparation.plans.length
      ? <div className="record-list">{preparation.plans.map((plan) => (
          <Card className={`care-plan-card care-plan-card--${plan.state}`} key={plan.id}>
            <div><strong>{plan.title}</strong><span>{plan.source.label} · {plan.source.checkedOn}</span></div>
            <div className="visit-meta"><small>{STATE_LABEL[plan.state]}</small>{plan.validUntil ? <small>~{plan.validUntil}</small> : null}</div>
          </Card>))}
        </div>
      : <p className="form-hint">{t('carePlan.planEmpty')}</p>}

    <div className="section-row">
      <h2>{t('carePlan.thresholdSection')}</h2>
      <Button variant="ghost" onClick={() => setOpenForm(openForm === 'threshold' ? null : 'threshold')}>
        {openForm === 'threshold' ? t('common.close') : t('carePlan.thresholdNew')}
      </Button>
    </div>
    <p className="form-hint">{t('carePlan.thresholdHint')}</p>
    {openForm === 'threshold'
      ? <form className="form-card" onSubmit={(event) => void saveThreshold(event)}>
          <label>{t('carePlan.thresholdValue')}<input required type="number" min="1" max="300" step="1" inputMode="numeric" value={thresholdValue} onChange={(event) => setThresholdValue(event.target.value)} /></label>
          <label>{t('carePlan.thresholdDirection')}
            <select value={thresholdDirection} onChange={(event) => setThresholdDirection(event.target.value as 'above' | 'below')}>
              <option value="above">{t('carePlan.thresholdAbove')}</option>
              <option value="below">{t('carePlan.thresholdBelow')}</option>
            </select>
          </label>
          <Button size="lg" type="submit">{t('common.save')}</Button>
        </form>
      : null}
    {thresholds.length
      ? <div className="record-list">{thresholds.map((threshold) => (
          <Card className="care-threshold-card" key={threshold.id}>
            <div><strong>{t('report.thresholdLabel', { value: threshold.value })}</strong>
              <span>{threshold.direction === 'above' ? t('carePlan.thresholdAbove') : t('carePlan.thresholdBelow')}</span>
            </div>
            <small>{t('carePlan.thresholdSetAt', { date: kstDate(threshold.setAt) })}</small>
          </Card>))}
        </div>
      : <p className="form-hint">{t('carePlan.thresholdEmpty')}</p>}

    <div className="section-row">
      <h2>{t('carePlan.supplySection')}</h2>
      <Button variant="ghost" onClick={() => setOpenForm(openForm === 'supply' ? null : 'supply')}>
        {openForm === 'supply' ? t('common.close') : t('carePlan.supplyNew')}
      </Button>
    </div>
    <p className="form-hint">{t('carePlan.supplyHint')}</p>
    {openForm === 'supply'
      ? <form className="form-card" onSubmit={(event) => void saveSupply(event)}>
          <label>{t('carePlan.supplyName')}<input required value={supplyName} maxLength={80} onChange={(event) => setSupplyName(event.target.value)} /></label>
          <label>{t('carePlan.supplyRemaining')}<input required type="number" min="0" step="0.1" inputMode="decimal" value={supplyRemaining} onChange={(event) => setSupplyRemaining(event.target.value)} /></label>
          <label>{t('carePlan.supplyUnit')}<input required value={supplyUnit} maxLength={20} onChange={(event) => setSupplyUnit(event.target.value)} /></label>
          <label>{t('carePlan.supplyCheckedOn')}<input required type="date" value={supplyCheckedOn} max={todayKst()} onChange={(event) => setSupplyCheckedOn(event.target.value)} /></label>
          <label>재고를 센 시각 (한국 시각)<input required={Boolean(supplyTreatmentId)} type="time" value={supplyCheckedTime} onChange={(event) => setSupplyCheckedTime(event.target.value)} /><small>이 시각보다 뒤에 준 약만 재고에서 빼요. 약과 연결할 때는 시각도 적어 주세요.</small></label>
          <label>{t('carePlan.supplyThreshold')}<input type="number" min="0" step="0.1" inputMode="decimal" value={supplyThreshold} onChange={(event) => setSupplyThreshold(event.target.value)} /></label>
          {treatmentOptions.length ? <><label>연결할 약 <span>선택</span><select value={supplyTreatmentId} onChange={(event) => setSupplyTreatmentId(event.target.value)}><option value="">연결 안 함</option>{treatmentOptions.map((option) => <option key={option.id} value={option.id}>{option.name}</option>)}</select></label>
          <label><input type="checkbox" checked={supplyNotify} onChange={e=>setSupplyNotify(e.target.checked)}/>약이 떨어지기 전에 알림</label>{supplyNotify?<label>남은 일수가 이만큼일 때<input type="number" min="0" max="30" value={supplyDays} onChange={e=>setSupplyDays(e.target.value)}/><small>알림·달력에서 웹 알림을 연결해 주세요.</small></label>:null}{supplyTreatmentId ? <label>1회에 쓰는 양<div className="input-with-unit"><input type="number" min="0.1" step="0.1" inputMode="decimal" value={supplyPerDose} onChange={(event) => setSupplyPerDose(event.target.value)} placeholder="예: 1" /><span>{supplyUnit}</span></div><small>연결하면 센 뒤 남긴 투약 기록만큼 빼서 남은 일수를 보여줘요.</small></label> : null}</> : null}
          <Button size="lg" type="submit">{t('common.save')}</Button>
        </form>
      : null}
    {supplySummary.length
      ? <div className="record-list">{supplySummary.map((supply) => (
          <Card className={`care-supply-card care-supply-card--${supply.state}`} key={supply.id}>
            <div><strong>{supply.name}</strong><span>{supply.remainingQuantity} {supply.unitLabel} · {supply.checkedOn}</span></div>
            <div className="visit-meta">
              <small>{supply.state === 'at_or_below_user_threshold' ? t('carePlan.supplyAttention') : t('carePlan.supplyCounted')}</small>
              {supply.thresholdLabel ? <small>{t('carePlan.supplyThreshold')}: {supply.thresholdLabel}</small> : null}
              <span className="care-supply-actions"><button type="button" onClick={() => editSupply(supply)}>{t('carePlan.supplyRecount')}</button><button type="button" onClick={() => void removeSupply(supply)}>{t('carePlan.supplyRemove')}</button></span>
            </div>
          </Card>))}
        </div>
      : <p className="form-hint">{t('carePlan.supplyEmpty')}</p>}

    <div className="section-row">
      <h2>{t('carePlan.questionSection')}</h2>
      <Button variant="ghost" onClick={() => setOpenForm(openForm === 'question' ? null : 'question')}>
        {openForm === 'question' ? t('common.close') : t('carePlan.questionNew')}
      </Button>
    </div>
    {openForm === 'question'
      ? <form className="form-card" onSubmit={(event) => void saveQuestion(event)}>
          <label>{t('carePlan.questionText')}<textarea required value={questionText} maxLength={500} rows={3} onChange={(event) => setQuestionText(event.target.value)} /></label>
          <Button size="lg" type="submit">{t('common.save')}</Button>
        </form>
      : null}
    {preparation.openQuestions.length
      ? <div className="record-list">{preparation.openQuestions.map((question) => (
          <Card className="care-question-card" key={question.id}>
            <div><strong>{question.text}</strong><span>{question.askedOn}</span></div>
            {question.carriedVisitCount > 0
              ? <small>{t('carePlan.questionCarried', { count: question.carriedVisitCount })}</small>
              : null}
          </Card>))}
        </div>
      : <p className="form-hint">{t('carePlan.questionEmpty')}</p>}

    <div className="section-row"><h2>{t('carePlan.groupSection')}</h2>
      <Button variant="ghost" onClick={() => setOpenForm(openForm === 'group' ? null : 'group')}>
        {openForm === 'group' ? t('common.close') : t('carePlan.groupNew')}
      </Button>
    </div>
    {openForm === 'group'
      ? <form className="form-card" onSubmit={(event) => void saveGroup(event)}>
          <label>{t('carePlan.groupTitle')}<input required value={groupTitle} maxLength={120} onChange={(event) => setGroupTitle(event.target.value)} /></label>
          <label>{t('carePlan.groupDates')}<input required value={groupDates} onChange={(event) => setGroupDates(event.target.value)} /></label>
          <p className="form-hint">{t('carePlan.groupDatesHint')}</p>
          <Button size="lg" type="submit">{t('common.save')}</Button>
        </form>
      : null}
    {preparation.eventGroups.length
      ? <div className="record-list">{preparation.eventGroups.map((group) => (
          <Card className="care-group-card" key={group.id}>
            <div><strong>{group.title}</strong>
              <span>{t('carePlan.groupSpan', { start: group.startDate, end: group.endDate, days: group.spanDays })}</span>
            </div>
          </Card>))}
        </div>
      : <p className="form-hint">{t('carePlan.groupEmpty')}</p>}

    {preparation.openQuestions.length
      ? <>
          <div className="section-row"><h2>{t('carePlan.closeSection')}</h2></div>
          <div className="form-card">
            <label>{t('carePlan.closeVisitedOn')}<input type="date" value={visitedOn} max={todayKst()} onChange={(event) => setVisitedOn(event.target.value)} /></label>
            {preparation.openQuestions.map((question) => (
              <label key={question.id}>{question.text}
                <textarea rows={2} maxLength={500} value={answers[question.id] ?? ''}
                  onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))} />
              </label>))}
            <p className="form-hint">{t('carePlan.closeHint')}</p>
            <Button size="lg" onClick={() => void saveAnswers()}>{t('carePlan.closeSave')}</Button>
          </div>
        </>
      : null}

    {preparation.answeredQuestions.length
      ? <>
          <div className="section-row"><h2>{t('carePlan.answeredSection')}</h2></div>
          <div className="record-list">{preparation.answeredQuestions.map((question) => (
            <Card className="care-question-card care-question-card--answered" key={question.id}>
              <div><strong>{question.text}</strong><span>{t('carePlan.answerLabel')}: {question.answer}</span></div>
              <small>{question.answeredOn}</small>
            </Card>))}
          </div>
        </>
      : null}

    <Card className="care-preparation-card">
      <h2>{t('carePlan.preparationSection')}</h2>
      <p>{t('carePlan.preparationHint')}</p>
      <div className="care-preparation-actions">
        <Button onClick={() => void copyPreparation()}>{t('carePlan.preparationCopy')}</Button>
        <Button variant="secondary" onClick={() => downloadText(`petrhythm-${pet.name}-${todayKst()}-care-pack.txt`, preparationText)}>{t('carePlan.preparationDownload')}</Button>
      </div>
    </Card>

    <p className="form-hint">{t('carePlan.disclaimer')}</p>
    {message ? <p className="toast" role="status">{message}</p> : null}
  </section>;
}
